<script setup>
import { ref, computed } from 'vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import { useI18n } from 'vue-i18n'
import useApi from '../../Composables/useApi'
import useDocumentSign from '../../../../Composables/DocumentSign/useDocumentSign'
import { useModalMessage } from '../../../../Composables/useModalMessage.ts'

const { t } = useI18n()
const { patchJSON } = useApi()
const { showSuccess } = useModalMessage()

const props = defineProps({
  document: {
    type: Object,
    required: true,
  },
  documentAssocId: {
    type: Number,
    required: true,
  },
  uuid: {
    type: String,
    required: false,
    default: null,
  },
  variant: {
    type: String,
    default: 'default',
  },
})

const { getDocumentSignerTypeLabel, getSignUrl, orderedSigners, signersWhoCanSign } = useDocumentSign(
  props.document.documentSign,
)

const signerTypeClasses = computed(() => {
  return (index) => {
    const classes = {
      'px-3 text-left col-md-2': true,
      'fs-18': props.variant !== 'documents_view',
      'fs-16': props.variant === 'documents_view',
    }

    return classes
  }
})

const signerNameClasses = computed(() => {
  return (index) => {
    const classes = {
      'px-3 d-flex justify-content-center align-items-center': true,
      'col-md-3': props.variant === 'documents_view',
      'col-md-2': props.variant !== 'documents_view',
      'fs-18': props.variant !== 'documents_view',
      'fs-16': props.variant === 'documents_view',
    }

    return classes
  }
})

const signButtonOptions = computed(() => {
  return {
    size: props.variant === 'documents_view' ? 'small' : null,
    icon: props.variant !== 'documents_view' ? 'outline_success' : null,
  }
})

const sendEmailButtonOptions = computed(() => {
  return {
    size: props.variant === 'documents_view' ? 'small' : null,
    icon: props.variant !== 'documents_view' ? 'outline_info' : null,
  }
})

const sending = ref(new Map())

const isSigned = (signFileStatus) => {
  return ['SIGNED', 'SIGNED_FULL'].includes(signFileStatus.status)
}

const tableClasses = computed(() => {
  let classes = 'table-striped _table-bordered table-hover fs-16 my-3 w-100'

  if (props.variant === 'documents_view') {
    classes = 'w-100 table-clean'
  }

  return classes
})

const rowClasses = computed(() => {
  return (index) => {
    const classes = {
      'py-2 row align-items-center': true,
    }

    if (props.variant === 'default' && index % 2 === 0) {
      classes['bg-light'] = true
    }

    return classes
  }
})

const emailClasses = computed(() => {
  return (index) => {
    const classes = {
      'px-3 col-md-3 d-flex justify-content-center align-items-center': true,
    }

    return classes
  }
})

const signedAtClasses = computed(() => {
  return (index) => {
    const classes = {
      'px-3 col-md-3 d-flex justify-content-center align-items-center': true,
    }

    return classes
  }
})

const actionClasses = computed(() => {
  return (index) => {
    const classes = {
      'px-3 d-flex justify-content-center align-items-center': true,
    }

    if (props.variant !== 'documents_view') {
      classes['col-md-5'] = true
    } else {
      classes['col-md-4'] = true
    }

    return classes
  }
})

const freeUrl = (url) => {
  if (!props.uuid) {
    return url
  }

  const separator = url.includes('?') ? '&' : '?'
  return `free/${url}${separator}uniqid=${props.uuid}`
}

const send = async ({ documentSigner }) => {
  sending.value.set(documentSigner.id, true)
  await patchJSON(freeUrl(`documentsigns/${props.documentAssocId}/send`), {
    documentSignerId: documentSigner.id,
    token: documentSigner.token,
  }).finally(() => {
    sending.value.delete(documentSigner.id)
  })
  showSuccess(t('contract.document.sendSign.signTo', { name: `${documentSigner?.fullname}` }))
}
</script>
<template>
  <div class="p-2 overflow-x-auto">
    <div
      v-for="(documentSigner, index) in orderedSigners"
      :key="index"
      :class="rowClasses(index)"
      :e2e-row="`document-signer-${documentSigner.id}`"
    >
      <div :class="signerTypeClasses(index)" e2e-data="documentSignerType">
        <i class="fas fa-user pr-2"></i>
        <span e2e-data="documentSignerTypeLabel">{{ getDocumentSignerTypeLabel(documentSigner) }}</span>
      </div>
      <div :class="signerNameClasses(index)" e2e-data="documentSignerName">
        <b>{{ documentSigner.name }}</b>
      </div>
      <div :class="emailClasses(index)" e2e-data="documentSignerEmail">
        {{ documentSigner.email }}
      </div>
      <div v-if="isSigned(documentSigner)" :class="signedAtClasses(index)" e2e-data="documentSignerSignedAt">
        <span v-if="variant !== 'documents_view'">
          {{ $t('document.signedAt', { date: $d(new Date(documentSigner.signedAt), 'datetime') }) }}
        </span>
        <i v-else class="fas fa-check text-success"></i>
      </div>
      <div v-else :class="actionClasses(index)">
        <ButtonAll
          margin="no"
          :icon="signButtonOptions.icon"
          :size="signButtonOptions.size"
          type="outline_success"
          :label="`contract.document.${documentSigner.validator ? 'validateNow' : 'signNow'}`"
          :href="getSignUrl(documentSigner)"
          :disabled="!signersWhoCanSign?.includes(documentSigner.id)"
          target="_blank"
          name="signNow"
        />
        <ButtonAll
          margin="no"
          :icon="sendEmailButtonOptions.icon"
          :size="sendEmailButtonOptions.size"
          type="outline_info"
          label="delivery.sendEmail"
          class="ml-4"
          :loading="sending.has(documentSigner.id)"
          :disabled="!signersWhoCanSign?.includes(documentSigner.id)"
          name="sendSignEmail"
          @click="send({ documentSigner })"
        />
      </div>
    </div>
    <slot></slot>
  </div>
</template>
